import { appointmentTypeKey } from '@/utils/appointmentFrequency';

const CANCELLABLE_TYPES = new Set(['routine', 'heart failure', 'combo', 'eri tracking']);
const ACTIVE_STATUSES = new Set(['scheduled', 'pending', 'no show']);

/** Patient schedule status "scheduled" already includes pending. No show is separate. */
export const ACTIVE_SCHEDULE_LOOKUP_STATUSES = ['scheduled', 'no show'] as const;
const ACTIVE_SCHEDULE_PAGE_SIZE = 100;
const ACTIVE_SCHEDULE_MAX_PAGES = 10;

/** Remote appointments the device cancel route will remove. In-clinic and physician-requested stay. */
export function deviceHasActiveSchedules(
  rows:
    | Array<{
        status?: string | null;
        type?: string | null;
        matchDeviceId?: string | null;
        device?: { id?: string | null } | null;
      }>
    | null
    | undefined,
  deviceId?: string | null
): boolean {
  if (!deviceId) return false;
  return (rows ?? []).some((row) => {
    const rowDeviceId = row.device?.id || row.matchDeviceId;
    return (
      rowDeviceId === deviceId &&
      ACTIVE_STATUSES.has(appointmentTypeKey(row.status)) &&
      CANCELLABLE_TYPES.has(appointmentTypeKey(row.type))
    );
  });
}

/**
 * Walks the patient schedule pages until this device has a cancellable row.
 * The schedules route caps each call at 100, and "scheduled" already returns pending.
 */
export async function findDeviceActiveSchedules(
  loadPage: (query: {
    status: (typeof ACTIVE_SCHEDULE_LOOKUP_STATUSES)[number];
    page: number;
    limit: number;
  }) => Promise<{
    data?: Parameters<typeof deviceHasActiveSchedules>[0];
    total?: number;
  }>,
  deviceId: string
): Promise<boolean> {
  if (!deviceId) return false;
  for (const status of ACTIVE_SCHEDULE_LOOKUP_STATUSES) {
    for (let page = 1; page <= ACTIVE_SCHEDULE_MAX_PAGES; page += 1) {
      const result = await loadPage({ status, page, limit: ACTIVE_SCHEDULE_PAGE_SIZE });
      const rows = result.data ?? [];
      if (deviceHasActiveSchedules(rows, deviceId)) return true;
      const total = result.total ?? rows.length;
      if (rows.length === 0 || page * ACTIVE_SCHEDULE_PAGE_SIZE >= total) break;
    }
  }
  return false;
}
